// "Döntések" (roadmap 3.6): what the Jarvis cannot settle alone — the weekly thread proposals, "is this the same
// person?", and every uncertain thread assignment. One tap each; every answer is a correction event.
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, Label, Panel } from '../ui';
import { PlacementCard } from './PlacementCard';
import { Guidance } from './Brain';

const PK: Record<string, string> = { thread_merge: 'Összevonás', thread_parent: 'Áthelyezés', thread_status: 'Lezárás', same_person: 'Ugyanaz az ember?', recurring_add: 'Ismétlődő tétel?', money_label: 'Mi ez a pénz?' };

/** "Mi ez a 12 500 Ft-os befizetés?": the likely answers as buttons, or his own words. */
function LabelCard({ p, onAnswer }: { p: any; onAnswer: (choice: string) => void }) {
  const [own, setOwn] = useState('');
  const amount = Number(p.payload.amount);
  return (
    <>
      <h3>{p.reason}</h3>
      <p className="small muted">{p.payload.date} · {amount > 0 ? '+' : '−'}{Math.abs(amount).toLocaleString('hu-HU')} Ft · {p.payload.merchant ?? ''} {p.payload.description ? `· ${p.payload.description}` : ''}</p>
      <div className="j-row">{(p.payload.options ?? []).map((o: string) => <button key={o} className="j-btn" onClick={() => onAnswer(o)}>{o}</button>)}</div>
      <form className="j-row quick-form" onSubmit={(e) => { e.preventDefault(); if (own.trim()) onAnswer(own.trim()); }}>
        <input className="j-input" value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Más: írd le, mi ez" /><button className="j-btn j-btn--primary">OK</button>
      </form>
    </>
  );
}

const SRC: Record<string, string> = { 'v1.discord_messages': 'Discord-üzenet', 'v1.transactions': 'fizetés', 'v1.neptun_messages': 'Neptun-üzenet', 'v1.tasks': 'teendő' };
/** Pending assignments grouped by thread and source (17 Discord messages of one channel are one decision). */
function groups(events: any[]) {
  const m = new Map<string, { key: string; thread: string; source: string; items: any[] }>();
  for (const x of events) {
    const key = `${x.thread_id}|${x.source}`;
    const g = m.get(key) ?? { key, thread: x.thread_id, source: x.source, items: [] as any[] };
    g.items.push(x); m.set(key, g);
  }
  return [...m.values()].sort((a, b) => b.items.length - a.items.length);
}

export function Decisions() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [shown, setShown] = useState(15);
  const [open, setOpen] = useState<string | null>(null);
  const [gone, setGone] = useState<Set<string>>(new Set());
  const load = useCallback(() => api('/api/decisions').then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); return onChange(load); }, [load]);
  const act = (p: Promise<unknown>) => p.then(load).catch((e) => setErr(e.message));
  const hide = (k: string, on: boolean) => setGone((g) => { const n = new Set(g); on ? n.add(k) : n.delete(k); return n; });
  /** the card goes at the tap; the server works in the background; on an error the card comes back */
  const quick = (k: string, p: () => Promise<unknown>) => { hide(k, true); p().then(load).catch((e) => { hide(k, false); setErr(e.message); }); };
  if (!d) return err ? <p className="err">{err}</p> : <p className="muted">Betöltés…</p>;

  return (
    <div className="j-stack">
      <Guidance onlyProposed />
      <Label c="coral">Döntések · {d.proposals.length + d.events.length}</Label>
      {d.proposals.filter((p: any) => !gone.has(p.id)).map((p: any) => (
        <Panel c={p.kind === 'same_person' ? 'mint' : 'violet'} className="j-dec" key={p.id}>
          <div className="meta">{PK[p.kind] ?? p.kind} · {day(p.created_at)}</div>
          {p.kind === 'money_label' ? <LabelCard p={p} onAnswer={(choice) => quick(p.id, () => api(`/api/proposals/${p.id}`, { accept: true, choice }))} /> : <>
          <h3>{p.kind === 'same_person' ? `${p.payload.a.name} = ${p.payload.b.name}?` : p.payload.label}</h3>
          <p>{p.reason}</p>
          <div className="j-row">
            <button className="j-btn j-btn--primary" onClick={() => quick(p.id, () => api(`/api/proposals/${p.id}`, { accept: true }))}>{p.kind === 'same_person' ? 'Igen, ugyanaz' : 'Elfogadom'}</button>
            <button className="j-btn" onClick={() => quick(p.id, () => api(`/api/proposals/${p.id}`, { accept: false }))}>Nem</button>
          </div></>}
        </Panel>
      ))}
      {d.events.length > 0 && <Label c="coral">Bizonytalan besorolás · {d.events.length}</Label>}
      {groups(d.events.filter((x: any) => !gone.has(x.event_id + x.thread_id) && !gone.has(`${x.thread_id}|${x.source}`))).map((g) => g.items.length >= 3 && open !== g.key ? (
        <div className="j-panel j-dec j-c-coral place-card" key={g.key}>
          <div className="meta">{g.items.length} {SRC[g.source] ?? 'esemény'}</div>
          <p className="ev">{g.items.slice(0, 3).map((x: any) => x.text).join(' · ').slice(0, 260)}…</p>
          <div className="meta">Ebben a szálban vannak</div>
          <h3>{g.items[0].parent ? <span className="parent">{g.items[0].parent} › </span> : null}{g.items[0].thread}</h3>
          <div className="meta ask-q">Mind ide tartozik?</div>
          <div className="j-row">
            <button className="j-btn j-btn--primary" onClick={() => quick(g.key, () => api('/api/decisions/bulk', { thread: g.thread, source: g.source }))}>Igen, mind</button>
            <button className="j-btn" onClick={() => setOpen(g.key)}>Egyenként</button>
          </div>
        </div>
      ) : g.items.slice(0, open === g.key ? 999 : shown).map((x: any) => <PlacementCard key={x.event_id + x.thread_id} x={x} onAnswer={(a) => quick(x.event_id + x.thread_id, () => api(`/api/decisions/${x.event_id}`, { thread: x.thread_id, action: a }))} onMoved={() => { hide(x.event_id + x.thread_id, true); load(); }} />))}
      {!d.proposals.length && !d.events.length && <p className="muted">Nincs döntésre váró ügy.</p>}
      {err && <p className="err">{err}</p>}
    </div>
  );
}
